import { cloneElement, useEffect, useState } from "react";
import { Head, Link, usePage } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import OrganizationDrawerForm from "@/pages/organizations/organization-drawer-form";
import Drawer from "@/components/drawer";
import { edit_manage_organization_faq_path, manage_organizations_path } from "@/routes";

function OrganizationsIndex({ organizations = [], onCreateOrganization = null, onEditOrganization = null }) {
  const { current_user: currentUser } = usePage().props;

  return (
    <>
      <Head title="Organisationer" />

      <section className="workspace-content">
        <section className="card">
          <div className="card-body">
            <div className="workspace-section__eyebrow">Administration</div>
            <h2 className="workspace-section__title">Organisationer</h2>
            <p className="workspace-admin-copy">
              Opret og vedligehold organisationer, og hold styr på hvem der har adgang til hvert arbejdsrum.
            </p>

            <div className="workspace-admin-table mt-4">
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Navn</th>
                      <th>Logo</th>
                      <th>CVR</th>
                      <th>By</th>
                      <th>Land</th>
                      <th>Brugere</th>
                      <th>Telefon</th>
                      <th className="text-end">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-muted py-4">
                          Der er ingen organisationer endnu.
                        </td>
                      </tr>
                    ) : (
                      organizations.map((organization) => (
                        <tr key={organization.id}>
                          <td>
                            <div className="workspace-admin-name">{organization.name}</div>
                            {organization.website ? (
                              <div className="text-muted">{organization.website}</div>
                            ) : null}
                          </td>
                          <td>
                            {organization.logo_url ? (
                              <img
                                src={organization.logo_url}
                                alt={`Logo for ${organization.name}`}
                                className="workspace-organization-logo"
                              />
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>{organization.cvr}</td>
                          <td>{organization.city}</td>
                          <td>{organization.country_code}</td>
                          <td>{organization.user_count}</td>
                          <td>{organization.phone}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              {currentUser?.super_admin ? (
                                <Link href={edit_manage_organization_faq_path(organization.id)} className="btn btn-outline-secondary">
                                  FAQ
                                </Link>
                              ) : null}
                              <button type="button" className="btn btn-outline-secondary" onClick={() => onEditOrganization?.(organization)}>
                                Redigér
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </section>
    </>
  );
}

function OrganizationsIndexLayout({ page }) {
  const {
    new_organization: newOrganization = null,
    create_errors: createErrors = [],
    show_create_organization_drawer: showCreateOrganizationDrawer = false,
    edit_organization: editOrganization = null,
    edit_errors: editErrors = [],
    show_edit_organization_drawer: showEditOrganizationDrawer = false
  } = page.props;
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(showCreateOrganizationDrawer);
  const [activeEditOrganization, setActiveEditOrganization] = useState(editOrganization);
  const [isCreateDrawerSubmitting, setIsCreateDrawerSubmitting] = useState(false);
  const [isEditDrawerSubmitting, setIsEditDrawerSubmitting] = useState(false);

  useEffect(() => {
    setIsCreateDrawerOpen(showCreateOrganizationDrawer);
  }, [showCreateOrganizationDrawer]);

  useEffect(() => {
    setActiveEditOrganization(editOrganization);
  }, [editOrganization]);

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Administration", href: manage_organizations_path() },
          { label: "Organisationer" }
        ],
        title: "Organisationer",
        actions: [
          {
            label: "Opret",
            variant: "primary",
            onClick: () => setIsCreateDrawerOpen(true)
          }
        ]
      }}
    >
      {cloneElement(page, {
        onCreateOrganization: () => setIsCreateDrawerOpen(true),
        onEditOrganization: (organization) => setActiveEditOrganization(organization)
      })}

      <Drawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        title="Opret organisation"
        size="md"
        closeDisabled={isCreateDrawerSubmitting}
      >
        <OrganizationDrawerForm
          organization={newOrganization}
          errors={createErrors}
          onSuccess={() => setIsCreateDrawerOpen(false)}
          onProcessingChange={setIsCreateDrawerSubmitting}
        />
      </Drawer>

      <Drawer
        isOpen={Boolean(activeEditOrganization)}
        onClose={() => setActiveEditOrganization(null)}
        title={activeEditOrganization ? `Redigér ${activeEditOrganization.name}` : "Redigér organisation"}
        size="md"
        closeDisabled={isEditDrawerSubmitting}
      >
        {activeEditOrganization ? (
          <OrganizationDrawerForm
            organization={activeEditOrganization}
            errors={showEditOrganizationDrawer && editOrganization?.id === activeEditOrganization.id ? editErrors : []}
            onSuccess={() => setActiveEditOrganization(null)}
            onProcessingChange={setIsEditDrawerSubmitting}
          />
        ) : null}
      </Drawer>
    </AuthLayout>
  );
}

OrganizationsIndex.layout = (page) => <OrganizationsIndexLayout page={page} />;

export default OrganizationsIndex;
